import { useState, useRef } from 'react';
import { Input } from '@/ui';
import * as s from './Combobox.css';

interface ComboboxProps {
  value: string | null;
  options: string[];
  onChange: (value: string | null) => void;
  placeholder: string;
  ariaLabel: string;
  /** Text shown when no option matches the query. Callers should pass a translated string. */
  emptyLabel?: string;
}

export function Combobox({ value, options, onChange, placeholder, ariaLabel, emptyLabel }: ComboboxProps) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const blurTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const text = open ? query : (value ?? '');
  const filtered = options.filter((o) => o.toLowerCase().includes(query.toLowerCase())).slice(0, 50);

  return (
    <div className={s.wrap}>
      <Input
        role="combobox"
        aria-label={ariaLabel}
        aria-expanded={open}
        value={text}
        placeholder={placeholder}
        onFocus={() => {
          setOpen(true);
          setQuery('');
        }}
        onChange={(e) => {
          setOpen(true);
          setQuery(e.target.value);
        }}
        onBlur={() => {
          blurTimer.current = setTimeout(() => setOpen(false), 120);
        }}
      />
      {open && (
        <div className={s.list} role="listbox">
          {filtered.length === 0 ? (
            <div className={s.empty}>{emptyLabel ?? 'No matches'}</div>
          ) : (
            filtered.map((o) => (
              <div
                key={o}
                role="option"
                aria-selected={o === value}
                className={s.option}
                onMouseDown={() => {
                  if (blurTimer.current) clearTimeout(blurTimer.current);
                }}
                onClick={() => {
                  onChange(o);
                  setOpen(false);
                  setQuery('');
                }}
              >
                {o}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}
